import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, endpoints, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { Loader2 } from "lucide-react";
import { postAuthDestination, normalizeIntent } from "@/lib/postAuthRouting";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Callback for the custom Project Mint Google OAuth flow.
// Path: /auth/google — Google redirects here with ?code=... & state=...
export default function GoogleCallback() {
    const nav = useNavigate();
    const { setUserAndArtists } = useAuth();
    const processed = useRef(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const err = params.get("error");

        (async () => {
            // Recover intent + next durably from the server. Falls back to
            // sessionStorage if the server round-trip fails (e.g. offline).
            let intent = "";
            let next = "/";
            let expectedState = "";
            try {
                expectedState = sessionStorage.getItem("pm_oauth_state") || "";
            } catch {}
            let serverIntent = null;
            if (state) {
                try {
                    serverIntent = await api.get("/auth/oauth-intent", { params: { state } })
                        .then((r) => r.data);
                } catch { serverIntent = null; }
            }
            if (serverIntent && (serverIntent.intent || serverIntent.next)) {
                intent = normalizeIntent(serverIntent.intent);
                if (serverIntent.next && serverIntent.next.startsWith("/")) next = serverIntent.next;
            } else {
                try {
                    next = sessionStorage.getItem("pm_oauth_next") || "/";
                    intent = normalizeIntent(sessionStorage.getItem("pm_signup_intent"));
                } catch {}
            }
            try {
                sessionStorage.removeItem("pm_oauth_next");
                sessionStorage.removeItem("pm_oauth_state");
                sessionStorage.removeItem("pm_signup_intent");
            } catch {}

            const nextQ = next && next !== "/" ? `&next=${encodeURIComponent(next)}` : "";

            if (err) {
                const bucket = err === "access_denied" ? "cancelled" : "provider_error";
                nav(`/signin?oauth=${bucket}${nextQ}`, { replace: true });
                return;
            }
            if (!code) {
                nav(`/signin?oauth=failed${nextQ}`, { replace: true });
                return;
            }
            if (!state || !expectedState || state !== expectedState) {
                nav(`/signin?oauth=invalid_state${nextQ}`, { replace: true });
                return;
            }

            const redirect_uri = window.location.origin + "/auth/google";
            try {
                const d = await endpoints.authGoogleCallback({ code, redirect_uri, intent: intent || undefined });
                await setUserAndArtists(d.user);

                // Persist Artist onboarding intent server-side IMMEDIATELY so
                // that even if the user closes the tab before /onboarding/artist
                // mounts, a fresh login on another device resumes correctly.
                if (intent === "artist") {
                    try {
                        await endpoints.upsertOnboardingState({ intent: "artist", status: "started" });
                    } catch { /* non-fatal: ArtistSetup will retry */ }
                }

                const dest = await postAuthDestination({
                    explicitNext: next && next !== "/" ? next : "",
                    intent: intent === "fan" ? "" : intent,
                });
                nav(dest, { replace: true });
            } catch (e) {
                const msg = formatApiError(e);
                let bucket = "failed";
                if (/expired/i.test(msg)) bucket = "expired";
                else if (/not configured|503/i.test(msg)) bucket = "failed";
                else if (/suspended/i.test(msg)) bucket = "link_error";
                setError(msg);
                nav(`/signin?oauth=${bucket}${nextQ}`, { replace: true });
            }
        })();
    }, [nav, setUserAndArtists]);

    return (
        <div className="min-h-[70vh] flex items-center justify-center" data-testid="google-callback">
            {error ? (
                <div className="max-w-md text-center px-6">
                    <div className="text-[10px] font-mono uppercase text-[#ff3333]">Sign-in couldn't complete</div>
                    <p className="mt-2 text-sm text-[#a3a3a3]" data-testid="google-callback-error">
                        We couldn't finish signing you in. Please try again from the sign-in page.
                    </p>
                </div>
            ) : (
                <div className="flex items-center gap-3 font-mono text-xs uppercase text-[#a3a3a3]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
                </div>
            )}
        </div>
    );
}
